import { mapValues, sum } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { ComponentDesignWithInformation, isTruss, Truss } from '@paragon/contract-library';
import { getNominalBoardFeet } from '@paragon/material-lists';
import { calculateMemberOverallLength } from '@paragon/three-shared';
import { ReadonlyRecord } from '@paragon/utilities';

import { HeightCache, WidthCache } from '../ComponentGeometryCaches';
import { AggregatedTrussStatistics } from '../components/pages/gridTypes';
import { RootState } from '../store';

export const getLoadedComponentDesignsWithInformation = createSelector(
  (state: RootState) => state.componentDesignsWithInformationByAnalysisSetGuid,

  (componentDesignsWithInformationByAnalysisSetGuid): ReadonlyRecord<string, ComponentDesignWithInformation> => {
    const entries = Object.entries(componentDesignsWithInformationByAnalysisSetGuid);
    const loadedComponentDesignsWithInformation: { [analysisSetGuid: string]: ComponentDesignWithInformation } = {};
    for (const [analysisSetGuid, componentDesignWithInformation] of entries) {
      if (componentDesignWithInformation === 'loading' || componentDesignWithInformation === 'error-loading') continue;
      loadedComponentDesignsWithInformation[analysisSetGuid] = componentDesignWithInformation;
    }
    return loadedComponentDesignsWithInformation;
  },
);

export const getLoadedTrussesByAnalysisSetGuid = createSelector(
  getLoadedComponentDesignsWithInformation,

  (loadedComponentDesignsWithInformationByAnalysisSetGuid): ReadonlyRecord<string, Truss> =>
    mapValues(loadedComponentDesignsWithInformationByAnalysisSetGuid, (componentDesignWithInformation) => {
      const truss = componentDesignWithInformation.componentDesign.component;
      if (!isTruss(truss)) throw new Error('Only trusses are supported');
      return truss;
    }),
);

export const getTrussMeasurements = createSelector(
  getLoadedTrussesByAnalysisSetGuid,

  (loadedTrussesByAnalysisSetGuid): ReadonlyRecord<string, AggregatedTrussStatistics> =>
    mapValues(loadedTrussesByAnalysisSetGuid, (truss, analysisSetGuid) => ({
      trussCount: 1,
      boardFeet:
        sum(
          truss.members.map((member) =>
            getNominalBoardFeet(member.lumber, calculateMemberOverallLength(member, truss) ?? 0),
          ),
        ) * truss.numberOfPlies,
      linearFeet: (WidthCache.get(analysisSetGuid, truss) * truss.numberOfPlies) / 12,
      heights: [HeightCache.get(analysisSetGuid, loadedTrussesByAnalysisSetGuid[analysisSetGuid])],
      widths: [WidthCache.get(analysisSetGuid, loadedTrussesByAnalysisSetGuid[analysisSetGuid])],
      thicknesses: [loadedTrussesByAnalysisSetGuid[analysisSetGuid].thickness],
    })),
);
